// The hero readouts (pool price and gap over the feed) from /api/data/latest or from the recorded
// snapshot with the direct reads on top.
import type { LatestSymbol } from '../api/types';
import type { FeedReading, PoolReading } from './feed';
import { fmtUsd, fmtUtc, fmtUtcS, parseTs, signedPct } from './format';
import type { Snapshot } from './snapshot';

export interface LastFeed { sym: string; at: Date; price: number; live: boolean }
export interface LiveReads { feed: FeedReading | null; pool: PoolReading | null }
export interface Readout { value: string; className: string; sub: string }
export interface Readouts { pool: Readout; gap: Readout; lastFeed: LastFeed | null }

/** Bell hook fee for the measured gap, one decimal ("hook fee 30.9 bps"): null means the worker could
 *  not derive it (stale feed and no plausible pool quote); it is shown as unknown, never as a number. */
export function hookFeeText(bps: number | null | undefined): string {
  return typeof bps === 'number' && Number.isFinite(bps) ? `hook fee ${bps.toFixed(1)} bps` : 'hook fee unknown';
}

const above = (gap: number): string => (gap >= 0 ? 'pool above the last feed price' : 'pool below the last feed price');

/** Hero readouts from /api/data/latest: one symbol entry. Symbols without a pool carry pair: null. */
export function readoutsFromApi(sym: string, s: LatestSymbol): Readouts {
  const feedAt = parseTs(s.oracle_updated_at), feedPrice = s.oracle;
  const lastFeed = feedAt && typeof feedPrice === 'number' ? { sym, at: feedAt, price: feedPrice, live: true } : null;
  if (s.pool == null || !s.pair || typeof feedPrice !== 'number') {
    return {
      lastFeed,
      pool: { value: 'no pool', className: '', sub: `no ${sym} pool with liquidity found on chain by the worker` },
      gap: { value: 'n/a', className: 'gap', sub: `${sym} feed ${typeof feedPrice === 'number' ? fmtUsd(feedPrice, 2) : 'n/a'} USD${feedAt ? `, ${fmtUtc(feedAt)}` : ''}` },
    };
  }
  const gap = typeof s.basis_pct === 'number' ? s.basis_pct : (s.pool / feedPrice - 1) * 100;
  const poolAt = parseTs(s.pool_ts);
  const venue = /uniswap/i.test(s.dex_source ?? '') ? 'Uniswap' : (s.dex_source ?? 'pool');
  return {
    lastFeed,
    pool: {
      value: fmtUsd(s.pool, 2) + ' USD',
      className: '',
      sub: `${venue}, read ${poolAt ? fmtUtcS(poolAt) : 'by the worker'}${s.liquidity_usd ? `, liquidity ${fmtUsd(s.liquidity_usd, 0)} USD` : ''}`,
    },
    gap: {
      value: signedPct(gap),
      className: gap >= 0 ? 'gap' : 'fresh',
      sub: `${above(gap)} (${sym} feed ${fmtUsd(feedPrice, 2)} USD, ${feedAt ? fmtUtc(feedAt) : 'time n/a'}), ${hookFeeText(s.bellguard_fee_bps)}`,
    },
  };
}

/** Hero readouts from the recorded snapshot, with the direct reads on top when they answered. */
export function readoutsFromSnapshot(sym: string, snap: Snapshot, live: LiveReads | null): Readouts | null {
  const s = snap.series[sym];
  if (!s) return null;
  const feedAt = live?.feed ? live.feed.updatedAt : new Date(s.oracle_updated_at);
  const feedPrice = live?.feed ? live.feed.price : s.last_oracle;
  const poolPrice = live?.pool ? live.pool.price : s.last_dex;
  const gap = (poolPrice / feedPrice - 1) * 100;
  return {
    lastFeed: { sym, at: feedAt, price: feedPrice, live: !!live?.feed },
    pool: {
      value: fmtUsd(poolPrice, 2) + ' USD',
      className: '',
      sub: live?.pool ? `Uniswap, read just now${live.pool.liquidity ? `, liquidity ${fmtUsd(live.pool.liquidity, 0)} USD` : ''}` : `recorded ${fmtUtc(new Date(s.last_ts))}`,
    },
    gap: {
      value: signedPct(gap),
      className: gap >= 0 ? 'gap' : 'fresh',
      sub: `${above(gap)} (${sym} feed ${fmtUsd(feedPrice, 2)} USD, ${fmtUtc(feedAt)})`,
    },
  };
}
